import React from 'react';
import {
  MenuItem,
  TextField,
  Autocomplete,
  createFilterOptions,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import {
  useCondition,
  type TargetInfo,
} from '../../../hooks/questionnaire/useCondition';
import { genderValues, CHRONIC_ILLNESSES } from '@aphp-ema/common/participant';
import { translatePatientDataValue } from '@/features/questionnaires/utils/translationHelpers';
import type { QuestionnaireFormData } from '@/features/questionnaires';
import { NumberInput, SelectInput, TextInput } from '@/shared';
import { DateInput } from '@/shared/components/forms/DateInput';

interface ConditionValueInputProps {
  questionIndex: number;
  conditionIndex: number;
  info: TargetInfo;
}

export const ConditionValueInput: React.FC<ConditionValueInputProps> = ({
  questionIndex,
  conditionIndex,
  info,
}) => {
  const form = useFormContext<QuestionnaireFormData>();
  const { control } = form;
  const { t } = useTranslation(['common', 'chronicIllnesses']);
  const { path } = useCondition(questionIndex, conditionIndex);

  const base = `${path}.conditions.${conditionIndex}` as const;
  const operator = useWatch({ control, name: `${base}.operator` });

  if (
    info.type === 'none' ||
    operator === 'is_empty' ||
    operator === 'is_not_empty'
  ) {
    return null;
  }

  if (operator === 'between') {
    let type = 'text';
    if (info.type === 'question') {
      if (info.questionType === 'number') type = 'number';
      if (info.questionType === 'date') type = 'date';
    } else if (info.type === 'patientData' && info.field === 'birthdate') {
      type = 'date';
    }

    if (type === 'number') {
      return (
        <>
          <NumberInput
            form={form}
            name={`${base}.minValue`}
            label={t('pages.studies.questions.conditions.minValue')}
            sx={{ minWidth: '200px' }}
          />
          <NumberInput
            form={form}
            name={`${base}.maxValue`}
            label={t('pages.studies.questions.conditions.maxValue')}
            sx={{ minWidth: '200px' }}
          />
        </>
      );
    }

    if (type === 'date') {
      return (
        <>
          <DateInput
            form={form}
            name={`${base}.minValue`}
            label={t('pages.studies.questions.conditions.minValue')}
            sx={{ minWidth: '200px' }}
          />
          <DateInput
            form={form}
            name={`${base}.maxValue`}
            label={t('pages.studies.questions.conditions.maxValue')}
            sx={{ minWidth: '200px' }}
          />
        </>
      );
    }
  }

  if (info.type === 'patientData') {
    if (info.field === 'birthdate') {
      return (
        <DateInput
          form={form}
          name={`${base}.value`}
          label={t('pages.studies.questions.conditions.value')}
          sx={{ minWidth: '200px' }}
        />
      );
    }

    if (info.field === 'gender') {
      return (
        <SelectInput
          form={form}
          name={`${base}.value`}
          label={t('pages.studies.questions.conditions.value')}
          sx={{ minWidth: '200px' }}
        >
          {genderValues.map((gender) => (
            <MenuItem key={gender} value={gender}>
              {translatePatientDataValue(t, 'gender', gender)}
            </MenuItem>
          ))}
        </SelectInput>
      );
    }

    if (info.field === 'chronicIllnesses') {
      const options = CHRONIC_ILLNESSES.map((illness) => ({
        value: illness,
        label: translatePatientDataValue(t, 'chronicIllnesses', illness),
      }));

      const filter = createFilterOptions({
        matchFrom: 'any',
        stringify: (option: { value: string; label: string }) => option.label,
      });

      return (
        <Controller
          control={control}
          name={`${base}.value`}
          render={({ field }) => (
            <Autocomplete
              {...field}
              value={
                field.value
                  ? options.find((o) => o.value === field.value)
                  : options[0]
              }
              onChange={(_, newValue) => {
                field.onChange(newValue?.value);
              }}
              options={options}
              getOptionLabel={(option) => option.label}
              filterOptions={filter}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('pages.studies.questions.conditions.value')}
                  required
                  sx={{ minWidth: '300px' }}
                />
              )}
              sx={{ minWidth: '300px' }}
              size="medium"
              disableClearable={false}
              autoHighlight
              openOnFocus
            />
          )}
        />
      );
    }
  }

  if (info.type === 'question') {
    const { questionType, questionParameters } = info;

    if (['radio', 'select', 'checkboxes'].includes(questionType)) {
      const options =
        'options' in questionParameters ? questionParameters.options : [];
      return (
        <SelectInput
          form={form}
          name={`${base}.value`}
          label={t('pages.studies.questions.conditions.value')}
          sx={{ minWidth: '200px' }}
        >
          {options.map((option, idx) => (
            <MenuItem key={idx} value={option.label}>
              {option.label}
            </MenuItem>
          ))}
        </SelectInput>
      );
    }

    if (questionType === 'number') {
      return (
        <NumberInput
          form={form}
          name={`${base}.value`}
          label={t('pages.studies.questions.conditions.value')}
          sx={{ minWidth: '200px' }}
        />
      );
    }

    if (questionType === 'date') {
      return (
        <DateInput
          form={form}
          name={`${base}.value`}
          label={t('pages.studies.questions.conditions.value')}
          sx={{ minWidth: '200px' }}
        />
      );
    }

    return (
      <TextInput
        form={form}
        name={`${base}.value`}
        label={t('pages.studies.questions.conditions.value')}
        sx={{ minWidth: '200px' }}
      />
    );
  }

  return null;
};

export default ConditionValueInput;
